/**
 * @voltxampere/ui — Largest Triangle Three Buckets (Sprint 15)
 *
 * The Sprint 15 waveform canvas is typically ~800-1000 px wide.
 * Drawing a polyline from 100k samples per channel through that
 * many `lineTo` calls saturates the rasteriser; LTTB picks one
 * sample per bucket — the one whose triangle area with the prior
 * bucket's chosen sample and the next bucket's average is largest
 * — so peaks survive while flat regions decimate cleanly.
 *
 * Reference: Sveinn Steinarsson, "Downsampling Time Series for
 * Visual Representation", MSc thesis, U. of Iceland, 2013.
 *
 * Decimation is deterministic — same input + same threshold give
 * the same output every time, which keeps regression tests stable.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
/**
 * Decimate a (time, value) series down to `threshold` points using
 * the Largest Triangle Three Buckets algorithm. When the input is
 * already smaller than the threshold, returns it unchanged (zero
 * cost — same Float64Array references).
 */
export function lttb(times, values, threshold) {
    const n = times.length;
    if (values.length !== n) {
        throw new Error(`lttb: time and value arrays must align (got times=${String(n)}, values=${String(values.length)})`);
    }
    if (threshold <= 2 || n <= threshold) {
        return { times, values };
    }
    const outTimes = new Float64Array(threshold);
    const outValues = new Float64Array(threshold);
    // Always include the first point.
    outTimes[0] = times[0] ?? 0;
    outValues[0] = values[0] ?? 0;
    const bucketSize = (n - 2) / (threshold - 2);
    let a = 0; // index of the previously selected sample.
    for (let i = 0; i < threshold - 2; i++) {
        // Range of the *next* bucket — used to compute the average
        // anchor for triangle area scoring.
        const avgRangeStart = Math.floor((i + 1) * bucketSize) + 1;
        const avgRangeEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, n);
        const avgRangeLen = Math.max(1, avgRangeEnd - avgRangeStart);
        let avgT = 0;
        let avgV = 0;
        for (let j = avgRangeStart; j < avgRangeEnd; j++) {
            avgT += times[j] ?? 0;
            avgV += values[j] ?? 0;
        }
        avgT /= avgRangeLen;
        avgV /= avgRangeLen;
        // Range of the current bucket — pick the sample with the
        // largest triangle area against `a` and the next bucket avg.
        const rangeStart = Math.floor(i * bucketSize) + 1;
        const rangeEnd = Math.min(Math.floor((i + 1) * bucketSize) + 1, n);
        const aT = times[a] ?? 0;
        const aV = values[a] ?? 0;
        let maxArea = -1;
        let maxIdx = rangeStart;
        for (let j = rangeStart; j < rangeEnd; j++) {
            const tj = times[j] ?? 0;
            const vj = values[j] ?? 0;
            // Triangle area from (aT, aV), (tj, vj), (avgT, avgV).
            const area = Math.abs((aT - avgT) * (vj - aV) - (aT - tj) * (avgV - aV)) * 0.5;
            if (area > maxArea) {
                maxArea = area;
                maxIdx = j;
            }
        }
        outTimes[i + 1] = times[maxIdx] ?? 0;
        outValues[i + 1] = values[maxIdx] ?? 0;
        a = maxIdx;
    }
    // Always include the last point.
    outTimes[threshold - 1] = times[n - 1] ?? 0;
    outValues[threshold - 1] = values[n - 1] ?? 0;
    return { times: outTimes, values: outValues };
}
//# sourceMappingURL=decimation.js.map